import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  JOB_FEED_APPROVED_EVENT,
  JOB_FEED_APPROVAL_MESSAGE,
} from '../../services/jobFeedApprovalService';

export const formatJobFeedNotificationAge = (createdAt, now = Date.now()) => {
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return 'Just now';
  const minutes = Math.max(0, Math.floor((now - created) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};

/**
 * NotificationProvider owns the socket but is mounted above BrowserRouter.
 * This small bridge lives inside the router, so an approval can remain an SPA
 * transition while the toast survives on the app-level ToastContainer.
 */
const JobFeedApprovalBridge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    const onApproved = (event) => {
      const notification = event?.detail || {};
      toast.success(
        <div>
          <strong>{notification.title || 'Your Job Feed was approved'}</strong>
          <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
            {notification.message || JOB_FEED_APPROVAL_MESSAGE}
          </p>
          <small className="text-muted">
            {formatJobFeedNotificationAge(notification.createdAt)}
          </small>
        </div>,
        {
          autoClose: 8000,
          toastId: `job-feed-approved:${
            notification.id || notification.data?.feedId || 'latest'
          }`,
        },
      );

      if (location.pathname !== '/company/listings') {
        // Give the invalidated feed reader one brief paint to replace
        // "Reviewing" with the server's Live card, then leave for the inventory
        // that the completed first pull just created.
        window.clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = window.setTimeout(() => {
          navigate('/company/listings', { replace: true });
        }, 500);
      }
    };

    window.addEventListener(JOB_FEED_APPROVED_EVENT, onApproved);
    return () => {
      window.removeEventListener(JOB_FEED_APPROVED_EVENT, onApproved);
      window.clearTimeout(redirectTimerRef.current);
    };
  }, [location.pathname, navigate]);

  return null;
};

export default JobFeedApprovalBridge;
